import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, InputText, FloatingField } from '../../components/aip';

// スタイルをインポート
import '../../styles/aip/index.css';

interface FieldErrors {
  email?: string;
}

/**
 * パスワードリセット申請ページ
 */
export const PasswordResetRequestPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  /**
   * フォームのバリデーション
   */
  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    if (!email.trim()) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = '有効なメールアドレスを入力してください';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length > 0;
  };

  /**
   * フィールドのエラーをクリア
   */
  const clearFieldError = (field: keyof FieldErrors) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setError(data.message || 'エラーが発生しました');
      }
    } catch {
      setError('通信エラーが発生しました。しばらく経ってから再度お試しください。');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="aip-html-body">
      <div className="aip-main" data-size="xs">
        <header className="aip-main__header">
          <div className="aip-main__header-container">
            <h1 className="aip-main__header-title">AiiPo Management</h1>
          </div>
        </header>

        <div className="aip-main__content">
          <h2 style={{ fontSize: '1.5rem', fontWeight: 500, marginBottom: '0.5rem' }}>
            パスワードの再発行
          </h2>

          {isSubmitted ? (
            // 送信完了メッセージ
            <div>
              <div
                style={{
                  padding: '1.5rem',
                  backgroundColor: 'var(--color-success)',
                  borderRadius: '0.5rem',
                  marginBottom: '1.5rem',
                }}
              >
                <p style={{ margin: 0, color: '#ffffff', fontWeight: 500 }}>
                  メールを送信しました
                </p>
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                  登録されているメールアドレスであれば、パスワード再設定用のリンクを送信しました。
                  メールをご確認ください。
                </p>
              </div>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                メールが届かない場合は、迷惑メールフォルダをご確認いただくか、
                しばらく経ってから再度お試しください。
              </p>
              <div style={{ textAlign: 'center' }}>
                <Link to="/meeting/manage-new/login" className="aip-link">
                  ログイン画面に戻る
                </Link>
              </div>
            </div>
          ) : (
            // 入力フォーム
            <>
              <p
                className="aip-main__content-description"
                style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}
              >
                登録されたメールアドレスに再設定用のリンクを送信します。
              </p>

              {error && (
                <div
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: 'var(--color-red50)',
                    color: 'var(--color-alert)',
                    borderRadius: '0.5rem',
                    marginBottom: '1rem',
                    fontSize: '0.875rem',
                  }}
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="aip-form-container__body">
                  {/* メールアドレス */}
                  <FloatingField
                    label="メールアドレス"
                    htmlFor="email"
                    column="2"
                    required
                    supportText="登録時に使用したメールアドレスを入力してください"
                    errorText={fieldErrors.email}
                    hasError={!!fieldErrors.email}
                  >
                    <InputText
                      id="email"
                      name="email"
                      type="email"
                      placeholder="example@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearFieldError('email');
                      }}
                      autoComplete="email"
                      className="aip-floating-field__control"
                      aria-invalid={!!fieldErrors.email}
                    />
                  </FloatingField>

                  {/* 送信ボタン */}
                  <div data-column="2" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <Button
                      buttonType="solid-fill"
                      size="md"
                      type="submit"
                      disabled={isLoading || !email}
                      style={{ width: '100%' }}
                    >
                      {isLoading ? '送信中...' : '送信'}
                    </Button>

                    <div style={{ textAlign: 'center' }}>
                      <Link to="/meeting/manage-new/login" className="aip-link">
                        ログイン画面に戻る
                      </Link>
                    </div>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordResetRequestPage;

